import {
  Firestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  increment,
  DocumentReference
} from "firebase/firestore";

const isExternalWorkflow = (status?: string | null): boolean => {
  if (!status) return false;
  return String(status).startsWith('wf_external');
};

async function getOrgIdFromLocation(db: Firestore, locationId: string): Promise<string | null> {
  const locRef = doc(db, "campLocations", locationId);
  const snap = await getDoc(locRef);
  if (!snap.exists()) return null;
  const data = snap.data() as any;
  const provided = data?.providedByOrg;
  if (!provided) return null;
  if (typeof provided === "string") return provided;
  if ((provided as DocumentReference)?.id) return (provided as DocumentReference).id;
  return null;
}

async function hasPublishedOffersForLocation(db: Firestore, locationId: string): Promise<boolean> {
  try {
    const locRef = doc(db, "campLocations", locationId);
    // Primary query by reference
    const offersRef = collection(db, "offers");
    const q1 = query(offersRef, where("locationRef", "==", locRef), where("visibility", "==", "published"));
    const snap1 = await getDocs(q1);
    if (!snap1.empty) return true;

    // Fallback query by string locationId if any old data remains
    const q2 = query(offersRef, where("locationId", "==", locationId), where("visibility", "==", "published"));
    const snap2 = await getDocs(q2);
    return !snap2.empty;
  } catch (e) {
    console.error("[orgPublishedLocationsCounter] Error checking published offers existence:", e);
    return false;
  }
}

export async function doesLocationQualifyForPublishedCount(
  db: Firestore,
  locationId: string,
  workflowStatusOverride?: string
): Promise<boolean> {
  const locRef = doc(db, "campLocations", locationId);
  const snap = await getDoc(locRef);
  if (!snap.exists()) return false;
  const data = snap.data() as any;
  const status = workflowStatusOverride ?? data?.workflowStatus;
  if (!isExternalWorkflow(status)) return false;
  const hasAny = await hasPublishedOffersForLocation(db, locationId);
  return hasAny;
}

async function adjustOrgCounter(db: Firestore, orgId: string, delta: number): Promise<void> {
  try {
    await updateDoc(doc(db, "campOrganizations", orgId), { numPublishedLocations: increment(delta) });
  } catch (e) {
    console.error("[orgPublishedLocationsCounter] Failed to adjust org counter:", { orgId, delta, error: e });
  }
}

export async function updateOrgCountForWorkflowChange(
  db: Firestore,
  locationId: string,
  oldWorkflowStatus?: string | null,
  newWorkflowStatus?: string | null
): Promise<void> {
  const prevQualified = await doesLocationQualifyForPublishedCount(db, locationId, oldWorkflowStatus || undefined);
  const nextQualified = await doesLocationQualifyForPublishedCount(db, locationId, newWorkflowStatus || undefined);
  if (prevQualified === nextQualified) return;
  const orgId = await getOrgIdFromLocation(db, locationId);
  if (!orgId) return;
  await adjustOrgCounter(db, orgId, nextQualified ? 1 : -1);
}

export async function updateOrgCountForDeletion(
  db: Firestore,
  locationId: string
): Promise<void> {
  const orgId = await getOrgIdFromLocation(db, locationId);
  if (!orgId) return;
  const qualified = await doesLocationQualifyForPublishedCount(db, locationId);
  if (qualified) {
    await adjustOrgCounter(db, orgId, -1);
  }
}

export async function updateOrgCountForOrgChange(
  db: Firestore,
  locationId: string,
  oldOrgId: string | null,
  newOrgId: string | null
): Promise<void> {
  if (oldOrgId === newOrgId) return;
  const qualified = await doesLocationQualifyForPublishedCount(db, locationId);
  if (!qualified) return;
  if (oldOrgId) {
    await adjustOrgCounter(db, oldOrgId, -1);
  }
  if (newOrgId) {
    await adjustOrgCounter(db, newOrgId, 1);
  }
}

export async function recomputeNumPublishedLocationsForOrg(
  db: Firestore,
  orgId: string
): Promise<number> {
  const orgRef = doc(db, "campOrganizations", orgId);
  // Find all locations for this org across possible storage formats of providedByOrg
  const locsRef = collection(db, "campLocations");
  const docsMap = new Map<string, any>();
  try {
    const qRef = query(locsRef, where("providedByOrg", "==", orgRef));
    const snapRef = await getDocs(qRef);
    for (const d of snapRef.docs) docsMap.set(d.id, d);
  } catch (e) {
    console.warn("[orgPublishedLocationsCounter] Query by DocumentReference failed", e);
  }
  try {
    const qStr = query(locsRef, where("providedByOrg", "==", orgId));
    const snapStr = await getDocs(qStr);
    for (const d of snapStr.docs) docsMap.set(d.id, d);
  } catch (e) {
    console.warn("[orgPublishedLocationsCounter] Query by string orgId failed", e);
  }
  try {
    // Older map-style storage: { id: orgId, path: 'campOrganizations/orgId' }
    const qMap = query(locsRef, where("providedByOrg.id", "==", orgId));
    const snapMap = await getDocs(qMap);
    for (const d of snapMap.docs) docsMap.set(d.id, d);
  } catch (e) {
    console.warn("[orgPublishedLocationsCounter] Query by providedByOrg.id failed", e);
  }

  let count = 0;
  for (const d of docsMap.values()) {
    const data: any = d.data();
    const wf: string | undefined = data?.workflowStatus;
    if (!isExternalWorkflow(wf)) continue;
    // Check offers existence
    const locRef = doc(db, "campLocations", d.id);
    const offersRef = collection(db, "offers");
    const q1 = query(offersRef, where("locationRef", "==", locRef), where("visibility", "==", "published"));
    const offersSnap = await getDocs(q1);
    if (!offersSnap.empty) {
      count += 1;
      continue;
    }
    // Fallback by string id
    const q2 = query(offersRef, where("locationId", "==", d.id), where("visibility", "==", "published"));
    const offersSnap2 = await getDocs(q2);
    if (!offersSnap2.empty) count += 1;
  }
  await updateDoc(orgRef, { numPublishedLocations: count });
  return count;
}


